/**
 * Distância em metros entre dois pontos WGS84 (fórmula de Haversine).
 * @param {number|string} lat1
 * @param {number|string} lng1
 * @param {number|string} lat2
 * @param {number|string} lng2
 * @returns {number} metros, ou NaN se alguma coordenada for inválida
 */
export function haversineMeters(lat1, lng1, lat2, lng2) {
  const aLat = Number(lat1);
  const aLng = Number(lng1);
  const bLat = Number(lat2);
  const bLng = Number(lng2);
  if (![aLat, aLng, bLat, bLng].every(Number.isFinite)) return NaN;

  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

const EARTH_RADIUS_M = 6371000;
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

/** Azimute inicial em graus (0=N, 90=E) de (lat1,lng1) para (lat2,lng2). */
export function bearingDegrees(lat1, lng1, lat2, lng2) {
  const aLat = Number(lat1);
  const aLng = Number(lng1);
  const bLat = Number(lat2);
  const bLng = Number(lng2);
  if (![aLat, aLng, bLat, bLng].every(Number.isFinite)) return 90;

  const φ1 = toRad(aLat);
  const φ2 = toRad(bLat);
  const Δλ = toRad(bLng - aLng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((toDeg(Math.atan2(y, x)) % 360) + 360) % 360;
}

/** Ponto a `distanceM` metros de (lat,lng) na direção `bearingDeg` (0=N, 90=E). */
export function destinationPointMeters(lat, lng, bearingDeg, distanceM) {
  const φ1 = toRad(Number(lat));
  const λ1 = toRad(Number(lng));
  const brng = toRad(Number(bearingDeg));
  const d = Number(distanceM) / EARTH_RADIUS_M;
  if (!Number.isFinite(φ1) || !Number.isFinite(λ1) || !Number.isFinite(d) || d <= 0) {
    return { lat: Number(lat), lng: Number(lng) };
  }

  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(brng)
  );
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(d) * Math.cos(φ1),
      Math.cos(d) - Math.sin(φ1) * Math.sin(φ2)
    );
  return { lat: toDeg(φ2), lng: ((toDeg(λ2) + 540) % 360) - 180 };
}

export default haversineMeters;
